import {
  createTheme,
  PaletteMode,
  PaletteOptions,
  ThemeOptions,
} from "@mui/material";
import { IconButtonPropsColorOverrides } from "@mui/material/IconButton";

declare module "@mui/material/styles" {
  interface Shape {
    borderRadius: number;
    borderRadiusSmall: number;
    borderRadiusMedium: number;
    borderRadiusLarge: number;
    borderRadiusRounded: number;
    borderRadiusSharp: number;
    borderRadiusCircle: string;
  }

  interface ShapeOptions {
    borderRadius?: number;
    borderRadiusSmall?: number;
    borderRadiusMedium?: number;
    borderRadiusLarge?: number;
    borderRadiusRounded?: number;
    borderRadiusSharp?: number;
    borderRadiusCircle?: string;
  }

  interface Palette {
    muted: Palette["primary"];
  }
  interface PaletteOptions {
    muted?: PaletteOptions["primary"];
  }

  interface TypographyVariants {
    Header: React.CSSProperties;
    Label: React.CSSProperties;
    SmallLabel: React.CSSProperties;
    SmallText: React.CSSProperties;
    ExtraSmallText: React.CSSProperties;
    ExtraSmallTextBold: React.CSSProperties;
    body3: React.CSSProperties;
  }

  // Allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    Header?: React.CSSProperties;
    Label?: React.CSSProperties;
    SmallLabel: React.CSSProperties;
    SmallText: React.CSSProperties;
    ExtraSmallText: React.CSSProperties;
    ExtraSmallTextBold: React.CSSProperties;
    body3: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    Header: true;
    Label: true;
    SmallLabel: true;
    SmallText: true;
    ExtraSmallText: true;
    ExtraSmallTextBold: true;
    body3: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    muted: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    muted: true; // Add your custom color key here
  }
}

const createCustomTheme = (
  mode: PaletteMode,
  customPalette: ThemeOptions["palette"]
) => {
  const palette: PaletteOptions = {
    mode: mode,
    common: {
      white: "#fff",
      black: "#000",
    },
    error: {
      main: "#D24E4E",
      light: "#FCEDE9",
      dark: "#A13636",
      contrastText: "#fff",
    },
    warning: {
      main: "#EE9500",
      light: "#FEF7EA",
      dark: "#B37000",
      contrastText: "#fff",
    },
    info: {
      main: "#006CE5",
      light: "#E6EFFA",
      dark: "#001F54",
      contrastText: "#fff",
    },
    success: {
      main: "#40BF62",
      light: "#EAF7EE",
      dark: "#26733B",
      contrastText: "#fff",
    },
    muted: {
      main: "#ececec",
      light: "#F5F5F5",
      dark: "#a6a6a6",
      contrastText: "#000000",
    },
    grey: {
      50: "#F5F5F5",
      100: "#ECECEC",
      200: "#E4E4E4",
      300: "#D6D6D6",
      400: "#BFBFBF",
      500: "#A6A6A6",
      600: "#919191",
      700: "#757779",
      800: "#535456",
      900: "#383838",
    },
    text: {
      primary: "#535456",
      secondary: "#757779",
      disabled: "#A6A6A6",
    },
    ...customPalette,
  };
  const baseTheme = createTheme({ palette: palette });

  return createTheme({
    ...baseTheme,
    shadows: [
      ...baseTheme.shadows.slice(0, 1), // Keep shadow 0 as default
      "0px 1px 3px 0px #00000040", // Override only shadow 1
      ...baseTheme.shadows.slice(2), // Keep shadows 2-24 as default
    ] as any,
    shape: {
      borderRadius: 4, // Default: 4px
      borderRadiusSmall: 8, // Small: 8px
      borderRadiusMedium: 10, // Medium: 10px
      borderRadiusLarge: 12, // Large: 12px
      borderRadiusRounded: 24, // Rounded: 24px
      borderRadiusSharp: 0, // Sharp: 0px
      borderRadiusCircle: "50%", // Circle: 50%
    } as any,
    typography: {
      fontFamily: "Poppins",
      h1: {
        fontSize: "1.5rem",
        fontWeight: 600,
      },
      h2: {
        fontSize: "1.375rem",
        fontWeight: 600,
      },
      Header: {
        fontSize: "0.875rem",
        fontWeight: 600,
      },
      subtitle1: {
        fontSize: "1.25rem",
        fontWeight: 500,
      },
      subtitle2: {
        fontSize: "1rem",
        fontWeight: 500,
      },
      body1: {
        fontSize: "1rem",
        fontWeight: 400,
      },
      body2: {
        fontSize: "0.875rem",
        fontWeight: 400,
      },
      Label: {
        fontSize: "0.75rem",
        fontWeight: 600,
        textTransform: "uppercase",
      },
      SmallLabel: {
        fontSize: "0.75rem",
        fontWeight: 500,
      },
      SmallText: {
        fontSize: "0.75rem",
        fontWeight: 400,
      },
      ExtraSmallTextBold: {
        //not using
        fontSize: "0.625rem",
        fontWeight: 500,
      },
      ExtraSmallText: {
        fontSize: "0.625rem",
        fontWeight: 400,
      },
      body3: {
        //not using
        fontSize: "0.875rem",
        fontWeight: 500,
      },
    },
  });
};

export default createCustomTheme;
